import React, { useState } from 'react';
import { ErrorHandler, LoadingState, TableTransfer } from '@common/components';
import { filterableColumnBuilder } from 'works/legacy/lib/table-filters-helper';
import { OrganizationUnitType } from '@institutions/graphql';
import { toUSCompactDateFormatWithTimeAndTZ } from 'works/legacy/lib/date-helper';
import { ApolloError } from '@common/hooks';

type Props = {
  selectedUnitIds: string[];
  onSelectUnitIdsChange: (selectedIds: string[]) => void;
  organizationUnits: OrganizationUnitType[] | undefined;
  loading: boolean;
  error: ApolloError | undefined;
};

type UnitType = {
  id: string;
  name: string;
  costCenter?: string | null;
  department: {
    id: string;
    organizationBusiness: {
      name: string;
    };
  };
  updatedAt: string;
  clinicalUnit: {
    displayName: string;
  };
};

const transferTitles = {
  leftSingular: 'Unit Available',
  leftPlural: 'Units Available',
  rightSingular: 'Unit Selected',
  rightPlural: 'Units Selected',
};

type Columns = {
  name: string;
  costCenter?: string | null;
  department: {
    organizationBusiness: {
      name: string;
    };
  };
  updatedAt: string;
  clinicalUnit: {
    displayName: string;
  };
};

function generateRowKey(data: UnitType[]) {
  return data.map((unit) => ({
    key: unit.id,
    selectKey: unit.department.id,
    ...unit,
  }));
}

const column = filterableColumnBuilder<Columns>({});

const tableColumns = [
  column({
    key: 'name',
    dataIndex: 'name',
    title: 'Unit Name',
  }),
  column({
    key: 'clinicalUnit',
    dataIndex: 'clinicalUnit',
    title: 'Unit Type',
    render: (clinicalUnit) => <span>{clinicalUnit?.displayName}</span>,
  }),
  column({
    key: 'costCenter',
    dataIndex: 'costCenter',
    title: 'Cost Center',
  }),
  column({
    key: 'department',
    dataIndex: 'department',
    title: 'Facility',
    render: (department) => (
      <span>{department?.organizationBusiness?.name}</span>
    ),
  }),
  column({
    key: 'updatedAt',
    dataIndex: 'updatedAt',
    title: 'Last Updated',
    render: (updatedAt) => (
      <span>{toUSCompactDateFormatWithTimeAndTZ(updatedAt)}</span>
    ),
  }),
];

export function SuppliersManagementBulkLocationsUpdateStepUnits({
  selectedUnitIds,
  onSelectUnitIdsChange,
  organizationUnits,
  loading,
  error,
}: Props) {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="SuppliersManagementBulkLocationsUpdateStepSuppliers"
      />
    );
  }

  if (loading || !organizationUnits) {
    return <LoadingState />;
  }

  function onSelectChange(
    sourceSelectedKeys: string[],
    targetSelectedKeys: string[]
  ) {
    const newSelectedKeys = [...sourceSelectedKeys, ...targetSelectedKeys];

    setSelectedKeys(newSelectedKeys);
  }

  return (
    <>
      <TableTransfer
        showSearch
        titles={transferTitles}
        selectKeyEnabled
        loading={loading}
        className="suppliers-management-bulk-locations-update-step-units"
        dataSource={generateRowKey(organizationUnits)}
        targetKeys={selectedUnitIds}
        selectedKeys={selectedKeys}
        onSelectChange={onSelectChange}
        disabled={false}
        onChange={onSelectUnitIdsChange}
        columns={tableColumns}
      />
      <style jsx>{`
        :global(.suppliers-management-bulk-locations-update-step-suppliers) {
          margin-top: 24px;
        }
      `}</style>
    </>
  );
}
